import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, Minus } from 'lucide-react';

const StepAdditionalCosts = ({
  additionalCosts,
  onAdditionalCostChange,
  totals,
  suggestedLoadAway,
  totalExcavatedArea,
  onBack,
  onContinue
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2">
        Additional Costs
      </h2>
      <p className="text-gray-600 mb-6">
        Add any additional costs for equipment and waste removal.
      </p>

      <div className="space-y-6">
        {/* Digger Required */}
        <div className={`border-2 rounded-xl p-5 transition-colors ${
          additionalCosts.diggerRequired ? 'border-[#300122] bg-[#300122]/5' : 'border-gray-200'
        }`}>
          <h3 className="font-bold text-[#27292E] mb-3">Is a digger required?</h3>
          <div className="flex gap-4 mb-4">
            <button
              onClick={() => onAdditionalCostChange('diggerRequired', true)}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                additionalCosts.diggerRequired === true
                  ? 'bg-[#300122] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="digger-yes"
            >
              Yes
            </button>
            <button
              onClick={() => onAdditionalCostChange('diggerRequired', false)}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                additionalCosts.diggerRequired === false
                  ? 'bg-[#300122] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="digger-no"
            >
              No
            </button>
          </div>
          
          {additionalCosts.diggerRequired && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <span className="text-gray-600">Digger Charge:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onAdditionalCostChange('diggerCost', Math.max(0, additionalCosts.diggerCost - 100))}
                  className="w-8 h-8 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-bold text-[#300122] text-xl min-w-[100px] text-center">
                  £{additionalCosts.diggerCost}
                </span>
                <button
                  onClick={() => onAdditionalCostChange('diggerCost', additionalCosts.diggerCost + 100)}
                  className="w-8 h-8 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Skip Required */}
        <div className={`border-2 rounded-xl p-5 transition-colors ${
          additionalCosts.skipRequired ? 'border-[#300122] bg-[#300122]/5' : 'border-gray-200'
        }`}>
          <h3 className="font-bold text-[#27292E] mb-3">Skip required?</h3>
          <div className="flex gap-4 mb-4">
            <button
              onClick={() => onAdditionalCostChange('skipRequired', true)}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                additionalCosts.skipRequired === true
                  ? 'bg-[#300122] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="skip-yes"
            >
              Yes
            </button>
            <button
              onClick={() => onAdditionalCostChange('skipRequired', false)}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                additionalCosts.skipRequired === false
                  ? 'bg-[#300122] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="skip-no"
            >
              No
            </button>
          </div>
          
          {additionalCosts.skipRequired && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <span className="text-gray-600">Skip Charge:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onAdditionalCostChange('skipCost', Math.max(0, additionalCosts.skipCost - 50))}
                  className="w-8 h-8 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-bold text-[#300122] text-xl min-w-[100px] text-center">
                  £{additionalCosts.skipCost}
                </span>
                <button
                  onClick={() => onAdditionalCostChange('skipCost', additionalCosts.skipCost + 50)}
                  className="w-8 h-8 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Load Away */}
        <div className={`border-2 rounded-xl p-5 transition-colors ${
          additionalCosts.loadAwayCount > 0 ? 'border-[#300122] bg-[#300122]/5' : 'border-gray-200'
        }`}>
          <h3 className="font-bold text-[#27292E] mb-1">Load Away</h3>
          <p className="text-sm text-gray-500 mb-4">
            £{additionalCosts.loadAwayCostPerLoad} per load
            {totalExcavatedArea > 0 && (
              <span className="text-[#300122] font-medium">
                {' '}• Suggested: {suggestedLoadAway} load{suggestedLoadAway !== 1 ? 's' : ''} (1 per 45m² excavated)
              </span>
            )}
          </p>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">Number of loads:</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onAdditionalCostChange('loadAwayCount', Math.max(0, additionalCosts.loadAwayCount - 1))}
                className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-bold text-[#300122] text-2xl min-w-[60px] text-center">
                {additionalCosts.loadAwayCount}
              </span>
              <button
                onClick={() => onAdditionalCostChange('loadAwayCount', additionalCosts.loadAwayCount + 1)}
                className="w-10 h-10 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          {additionalCosts.loadAwayCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-gray-600">
                {additionalCosts.loadAwayCount} load{additionalCosts.loadAwayCount !== 1 ? 's' : ''} × £{additionalCosts.loadAwayCostPerLoad}
              </span>
              <span className="font-bold text-[#300122] text-xl">
                £{(additionalCosts.loadAwayCount * additionalCosts.loadAwayCostPerLoad).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Running Total */}
      <div className="mt-6 p-4 bg-[#300122] text-white rounded-xl">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-white/80">Surfacing:</span>
            <span>£{totals.surfacingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
          </div>
          {totals.additionalTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Additional Costs:</span>
              <span>£{totals.additionalTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-white/20">
            <span className="font-bold">Total:</span>
            <span className="text-2xl font-bold">
              £{totals.grandTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          onClick={onBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={onContinue}
          className="flex-1 bg-[#300122] hover:bg-[#4a0636] text-white h-14 text-lg font-bold rounded-xl"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default StepAdditionalCosts;
